import { useEffect, useMemo, useRef, useState } from 'react'
import { Pause, Play, RotateCcw, Square } from 'lucide-react'

const chunkText = (text: string, size = 900) => {
  const paragraphs = text.split(/\n+/).filter(Boolean)
  const chunks: string[] = []
  let current = ''
  paragraphs.forEach((paragraph) => {
    if (`${current}\n${paragraph}`.length > size && current) {
      chunks.push(current)
      current = paragraph
    } else current = `${current}\n${paragraph}`.trim()
  })
  if (current) chunks.push(current)
  return chunks
}

export const TextToSpeech = ({ text }: { text: string }) => {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const [voiceIndex, setVoiceIndex] = useState(0)
  const [rate, setRate] = useState(1)
  const [playing, setPlaying] = useState(false)
  const [currentChunk, setCurrentChunk] = useState(0)
  const generationRef = useRef(0)
  const chunks = useMemo(() => chunkText(text), [text])

  useEffect(() => {
    const load = () => {
      const available = speechSynthesis.getVoices()
      setVoices(available)
      const preferred = available.findIndex((voice) => voice.lang.startsWith('kk') || voice.lang.startsWith('ru'))
      if (preferred >= 0) setVoiceIndex(preferred)
    }
    load()
    speechSynthesis.addEventListener('voiceschanged', load)
    return () => speechSynthesis.removeEventListener('voiceschanged', load)
  }, [])

  const stop = () => {
    generationRef.current += 1
    speechSynthesis.cancel()
    setPlaying(false)
    setCurrentChunk(0)
  }

  const speakFrom = (index: number) => {
    if (!chunks.length) return
    const generation = ++generationRef.current
    speechSynthesis.cancel()
    setPlaying(true)
    const speakChunk = (chunkIndex: number) => {
      if (generation !== generationRef.current || chunkIndex >= chunks.length) {
        setPlaying(false)
        return
      }
      setCurrentChunk(chunkIndex)
      const utterance = new SpeechSynthesisUtterance(chunks[chunkIndex])
      utterance.voice = voices[voiceIndex] || null
      utterance.rate = rate
      utterance.onend = () => speakChunk(chunkIndex + 1)
      utterance.onerror = () => setPlaying(false)
      speechSynthesis.speak(utterance)
    }
    speakChunk(index)
  }

  return (
    <section className="panel">
      <div className="panel-heading"><div><p className="eyebrow">МӘТІНДІ ТЫҢДАУ</p><h3>Ұзақ мәтінді үзбей оқу</h3></div><span className="counter">{chunks.length ? currentChunk + 1 : 0}/{chunks.length}</span></div>
      <div className="form-grid">
        <label>Дауыс<select value={voiceIndex} onChange={(event) => setVoiceIndex(Number(event.target.value))}>{voices.map((voice, index) => <option value={index} key={`${voice.name}-${index}`}>{voice.name} · {voice.lang}</option>)}</select></label>
        <label>Жылдамдық: {rate.toFixed(1)}×<input type="range" min="0.6" max="1.8" step="0.1" value={rate} onChange={(event) => setRate(Number(event.target.value))} /></label>
      </div>
      <div className="button-row">
        {!playing && <button className="primary" disabled={!text.trim()} onClick={() => speakFrom(currentChunk)}><Play size={17} /> Оқу</button>}
        {playing && <button className="secondary" onClick={() => speechSynthesis.paused ? speechSynthesis.resume() : speechSynthesis.pause()}><Pause size={17} /> Кідірту / жалғастыру</button>}
        <button className="danger" onClick={stop}><Square size={16} /> Тоқтату</button>
        <button className="ghost" onClick={() => { stop(); setCurrentChunk(0) }}><RotateCcw size={16} /> Басынан</button>
      </div>
    </section>
  )
}
